import { PostMessageBridge } from "./bridge/post-message-bridge";
import { CommandType, EventType } from "./bridge/protocol";
import type { BridgeMessage } from "./bridge/types";
import { getWidgetUrl } from "./config";
import {
  DEFAULT_BRIDGE_TIMEOUT,
  OPEN_FALLBACK_TIMEOUT,
  WIDGET_READY_TIMEOUT,
} from "./constants";
import { ConfigurationError, ValidationError } from "./errors";
import { EventEmitter } from "./events/event-emitter";
import type {
  AgentWidgetConfig,
  Message,
  WidgetConfig,
  WidgetMode,
  WidgetOptions,
  WidgetState,
} from "./types";
import { WidgetRenderer } from "./widget/widget-renderer";
import { WidgetStateManager } from "./widget/widget-state";

/**
 * YampaWidget is the main class for integrating the Yampa AI chat widget into your application.
 *
 * @extends EventEmitter
 *
 * @example
 * ```typescript
 * // Create a fullscreen widget
 * const widget = new YampaWidget({
 *   apiKey: 'your-api-key',
 *   mode: 'fullscreen',
 *   container: '#chat-container'
 * });
 *
 * // Listen for events
 * widget.on('ready', () => console.log('Widget is ready'));
 * widget.on('message:received', ({ message }) => console.log('Received:', message));
 *
 * // Open the widget when ready
 * await widget.open();
 * ```
 *
 * @example
 * ```typescript
 * // Create a bubble widget
 * const widget = new YampaWidget({
 *   apiKey: 'your-api-key',
 *   mode: 'bubble',
 *   bubble: {
 *     width: 400,
 *     height: 600,
 *     position: { horizontal: 'right', vertical: 'bottom' }
 *   }
 * });
 * ```
 *
 * @fires YampaWidget#ready - When the widget is initialized and ready to receive commands
 * @fires YampaWidget#open - When the widget is opened
 * @fires YampaWidget#close - When the widget is closed
 * @fires YampaWidget#error - When an error occurs
 * @fires YampaWidget#message:sent - When a message is sent
 * @fires YampaWidget#message:received - When a message is received
 * @fires YampaWidget#ai:streaming - When AI is streaming a response token
 * @fires YampaWidget#ai:done - When AI finishes streaming a response
 * @fires YampaWidget#conversation:started - When a new conversation is started
 * @fires YampaWidget#config:updated - When the configuration is updated
 */
export class YampaWidget extends EventEmitter {
  private readonly bridge: PostMessageBridge;
  private config: WidgetConfig;
  private readonly stateManager: WidgetStateManager;
  private readonly renderer: WidgetRenderer;
  private readonly mode: WidgetMode;
  private agentCustomData: Record<string, unknown>;
  private readonly stream: boolean;
  private readonly overrides?: Partial<AgentWidgetConfig>;

  /**
   * Creates a new YampaWidget instance.
   *
   * @param options - Configuration options for the widget
   * @throws {ConfigurationError} If apiKey is missing or invalid
   *
   * @example
   * ```typescript
   * const widget = new YampaWidget({
   *   apiKey: 'your-api-key',
   *   mode: 'bubble',
   *   stream: true,
   *   agentCustomData: { userId: '123' }
   * });
   * ```
   */
  constructor(options: WidgetOptions) {
    super();

    if (
      !options.apiKey ||
      typeof options.apiKey !== "string" ||
      options.apiKey.trim() === ""
    ) {
      throw new ConfigurationError(
        "apiKey is required and must be a non-empty string",
        "MISSING_API_KEY"
      );
    }

    const widgetUrl = options.widgetUrl || getWidgetUrl();
    this.mode = options.mode || "fullscreen";
    this.agentCustomData = options.agentCustomData || {};
    this.stream = options.stream ?? false;
    this.overrides = options.overrides;

    this.config = {
      apiKey: options.apiKey,
      widgetUrl,
      mode: this.mode,
      bubble: options.bubble,
      theme: {
        ...options.theme,
        primaryColor:
          options.theme?.primaryColor || options.overrides?.primary_color || undefined,
      },
    };

    this.stateManager = new WidgetStateManager(this.mode);
    this.renderer = new WidgetRenderer(this.mode, this.config);

    this.bridge = new PostMessageBridge({
      timeout: DEFAULT_BRIDGE_TIMEOUT,
      targetOrigin: new URL(widgetUrl).origin,
      onEvent: (event) => {
        this.handleBridgeEvent(event);
      },
    });

    this.init(options.container);
  }

  private init(container?: string | HTMLElement): void {
    const iframe = this.renderer.init(container);
    this.bridge.init(iframe);

    if (this.mode === "bubble") {
      const wrapper = this.renderer.getWrapper();
      if (wrapper) {
        wrapper.addEventListener("click", async (e) => {
          if (this.shouldHandleBubbleClick(e, wrapper)) {
            e.stopPropagation();
            await this.handleBubbleClickOpen();
          }
        });

        const iframe = this.renderer.getIframe();
        if (iframe) {
          iframe.addEventListener("load", () => {
            this.bridge.sendCommand({
              type: CommandType.UPDATE_CONFIG,
              payload: { mode: "bubble" },
            });
          });
        }
      }
    }
  }

  /**
   * Opens the widget.
   * In bubble mode, expands the bubble to show the chat interface.
   * In fullscreen mode, makes the widget visible.
   *
   * If the widget is not yet ready, this method will wait for the 'ready' event
   * before opening (with a 30 second timeout).
   *
   * @throws {ValidationError} If the widget does not become ready within the timeout
   * @fires YampaWidget#open - When the widget is successfully opened
   * @fires YampaWidget#error - If opening fails
   *
   * @example
   * ```typescript
   * // Wait for ready, then open
   * widget.on('ready', async () => {
   *   await widget.open();
   *   console.log('Widget is now open');
   * });
   * ```
   *
   * @example
   * ```typescript
   * // Or simply call open() - it will wait for ready automatically
   * try {
   *   await widget.open();
   * } catch (error) {
   *   console.error('Failed to open widget:', error);
   * }
   * ```
   */
  async open(): Promise<void> {
    if (this.stateManager.isOpen()) {
      return;
    }

    if (!this.stateManager.isReady()) {
      if (this.bridge.ready && !this.stateManager.isReady()) {
        this.stateManager.setIsReady(true);
        this.emit("ready", undefined);
      } else {
        await new Promise<void>((resolve, reject) => {
          let resolved = false;
          const timeout = setTimeout(() => {
            if (!resolved) {
              resolved = true;
              this.off("ready", onReady);
              reject(
                new ValidationError(
                  "Widget did not become ready within timeout. Wait for the 'ready' event before opening.",
                  "WIDGET_NOT_READY"
                )
              );
            }
          }, WIDGET_READY_TIMEOUT);

          const onReady = () => {
            if (!resolved) {
              resolved = true;
              clearTimeout(timeout);
              this.off("ready", onReady);
              resolve();
            }
          };

          if (this.stateManager.isReady()) {
            resolved = true;
            clearTimeout(timeout);
            resolve();
          } else {
            this.on("ready", onReady);
          }
        });
      }
    }

    if (this.mode === "bubble") {
      this.renderer.setBubbleCollapsed(false);
    }

    try {
      await this.bridge.sendCommand({ type: CommandType.OPEN });

      let openedReceived = false;
      const openedTimeout = setTimeout(() => {
        if (!(openedReceived || this.stateManager.isOpen())) {
          this.stateManager.setIsOpen(true);
          this.emit("open", undefined);
        }
      }, OPEN_FALLBACK_TIMEOUT);

      const onOpened = () => {
        openedReceived = true;
        clearTimeout(openedTimeout);
        this.off("open", onOpened);
      };
      this.once("open", onOpened);
    } catch (error) {
      if (this.mode === "bubble") {
        this.renderer.setBubbleCollapsed(true);
      }
      this.emit("error", {
        error: error instanceof Error ? error.message : "Unknown error",
        code: "OPEN_FAILED",
      });
      throw error;
    }
  }

  /**
   * Closes the widget.
   * In bubble mode, collapses the chat back to the bubble indicator.
   * In fullscreen mode, hides the widget.
   *
   * Does nothing if the widget is already closed.
   *
   * @fires YampaWidget#close - When the widget is successfully closed
   * @fires YampaWidget#error - If closing fails
   *
   * @example
   * ```typescript
   * await widget.close();
   * console.log('Widget closed');
   * ```
   */
  async close(): Promise<void> {
    if (!this.stateManager.isOpen()) {
      return;
    }

    if (this.mode === "bubble") {
      this.renderer.setBubbleCollapsed(true);
    }

    try {
      await this.bridge.sendCommand({ type: CommandType.CLOSE });
      this.stateManager.setIsOpen(false);
      this.emit("close", undefined);
    } catch (error) {
      if (this.mode === "bubble") {
        this.renderer.setBubbleCollapsed(false);
      }
      this.emit("error", {
        error: error instanceof Error ? error.message : "Unknown error",
        code: "CLOSE_FAILED",
      });
      throw error;
    }
  }

  /**
   * Toggles the widget open/closed state.
   * If open, closes the widget. If closed, opens the widget.
   *
   * @fires YampaWidget#open - When the widget is opened
   * @fires YampaWidget#close - When the widget is closed
   *
   * @example
   * ```typescript
   * // Toggle on button click
   * button.addEventListener('click', () => widget.toggle());
   * ```
   */
  async toggle(): Promise<void> {
    if (this.stateManager.isOpen()) {
      await this.close();
    } else {
      await this.open();
    }
  }

  /**
   * Sends a message to the AI agent through the widget.
   *
   * @param message - The message text to send (must be non-empty)
   * @throws {ValidationError} If the message is empty or widget is not ready
   * @fires YampaWidget#message:sent - When the message is successfully sent
   * @fires YampaWidget#error - If sending fails
   *
   * @example
   * ```typescript
   * widget.on('ready', async () => {
   *   await widget.sendMessage('Hello, how can you help me?');
   * });
   * ```
   *
   * @example
   * ```typescript
   * // Listen for the response
   * widget.on('ai:streaming', ({ token }) => {
   *   process.stdout.write(token);
   * });
   *
   * widget.on('ai:done', ({ message }) => {
   *   console.log('Full response:', message.content);
   * });
   *
   * await widget.sendMessage('What is the weather like?');
   * ```
   */
  async sendMessage(message: string): Promise<void> {
    if (!message || typeof message !== "string" || !message.trim()) {
      throw new ValidationError("Message cannot be empty", "EMPTY_MESSAGE");
    }

    if (!this.stateManager.isReady()) {
      throw new ValidationError(
        "Widget is not ready. Wait for the 'ready' event before sending messages.",
        "WIDGET_NOT_READY"
      );
    }

    try {
      await this.bridge.sendCommand({
        type: CommandType.SEND_MESSAGE,
        payload: { message },
      });
    } catch (error) {
      this.emit("error", {
        error: error instanceof Error ? error.message : "Unknown error",
        code: "SEND_MESSAGE_FAILED",
      });
      throw error;
    }
  }

  /**
   * Injects an agent message directly into the chat without calling the AI backend.
   * Useful for scripted onboarding flows, handoffs, or other cases where the host
   * page needs to render an agent message on behalf of the user.
   *
   * @param content - The agent message content (must be a non-empty string)
   * @throws {ValidationError} If the content is empty or the widget is not ready
   *
   * @example
   * ```typescript
   * widget.on('ready', async () => {
   *   await widget.displayAgentMessage("Welcome! How can I help?");
   * });
   * ```
   */
  async displayAgentMessage(content: string): Promise<void> {
    if (!content || typeof content !== "string" || !content.trim()) {
      throw new ValidationError(
        "Agent message cannot be empty",
        "EMPTY_MESSAGE"
      );
    }

    if (!this.stateManager.isReady()) {
      throw new ValidationError(
        "Widget is not ready. Wait for the 'ready' event before sending messages.",
        "WIDGET_NOT_READY"
      );
    }

    await this.bridge.sendCommand({
      type: CommandType.INJECT_AGENT_MESSAGE,
      payload: { content },
    });
  }

  /**
   * Updates the widget configuration at runtime.
   * Changes are applied immediately and synced with the embedded widget.
   *
   * @param config - Partial configuration to merge with current config
   * @fires YampaWidget#config:updated - When the configuration is updated
   *
   * @example
   * ```typescript
   * // Update theme colors
   * widget.updateConfig({
   *   theme: {
   *     primaryColor: '#ff6600',
   *     backgroundColor: '#1a1a1a'
   *   }
   * });
   * ```
   *
   * @example
   * ```typescript
   * // Update bubble dimensions
   * widget.updateConfig({
   *   bubble: {
   *     width: 500,
   *     height: 700
   *   }
   * });
   * ```
   */
  updateConfig(config: Partial<WidgetConfig>): void {
    this.config = { ...this.config, ...config };

    this.renderer.updateConfig(this.config);

    if (this.mode === "bubble" && config.bubble) {
      this.renderer.updateBubbleConfig(config.bubble);
    }

    if (config.mode) {
      this.stateManager.setMode(config.mode);
    }

    this.bridge.sendCommand({
      type: CommandType.UPDATE_CONFIG,
      payload: config,
    });

    this.emit("config:updated", { config: this.config });
  }

  /**
   * Gets the current state of the widget.
   *
   * Returns a synchronous snapshot of SDK-cached state. SDK-owned fields
   * (`isOpen`, `isReady`) are always accurate; iframe-owned fields
   * (`conversationId`, `isStreaming`, `messageCount`, `mode`) are kept in sync
   * via events. If you suspect drift (e.g. the iframe was mounted before the
   * SDK attached), call {@link YampaWidget.syncState} to refresh from the
   * iframe.
   *
   * @returns A readonly copy of the widget state
   *
   * @example
   * ```typescript
   * const state = widget.getState();
   * console.log('Is open:', state.isOpen);
   * console.log('Is ready:', state.isReady);
   * console.log('Is streaming:', state.isStreaming);
   * console.log('Is ended:', state.isClosed);
   * console.log('Mode:', state.mode);
   * console.log('Message count:', state.messageCount);
   * console.log('Conversation ID:', state.conversationId);
   * ```
   */
  getState(): Readonly<WidgetState> {
    return this.stateManager.getState();
  }

  /**
   * Forces a one-shot synchronization of iframe-owned state
   * (`conversationId`, `isStreaming`, `mode`, `messageCount`) from the embedded
   * widget. Useful when the SDK may have missed events (e.g. late mount,
   * reload) or when you want a guaranteed-fresh snapshot.
   *
   * SDK-owned fields (`isOpen`, `isReady`) are not touched.
   *
   * Does nothing if the widget is not ready yet; the SDK automatically calls
   * this once on `ready` to seed state.
   *
   * @returns The refreshed widget state
   *
   * @example
   * ```typescript
   * const state = await widget.syncState();
   * ```
   */
  async syncState(): Promise<Readonly<WidgetState>> {
    if (!this.stateManager.isReady()) {
      return this.stateManager.getState();
    }

    try {
      const result = await this.bridge.sendCommand({
        type: CommandType.GET_STATE,
      });

      if (result && typeof result === "object") {
        const partial: Partial<
          Pick<
            WidgetState,
            "isStreaming" | "isClosed" | "mode" | "messageCount" | "conversationId"
          >
        > = {};
        const r = result as Record<string, unknown>;
        if (typeof r.isStreaming === "boolean") {
          partial.isStreaming = r.isStreaming;
        }
        if (typeof r.isClosed === "boolean") {
          partial.isClosed = r.isClosed;
        }
        if (r.mode === "bubble" || r.mode === "fullscreen") {
          partial.mode = r.mode;
        }
        if (typeof r.messageCount === "number") {
          partial.messageCount = r.messageCount;
        }
        if (typeof r.conversationId === "string") {
          partial.conversationId = r.conversationId;
        } else if (r.conversationId === undefined || r.conversationId === null) {
          partial.conversationId = undefined;
        }
        this.stateManager.mergeRemoteState(partial);
      }
    } catch {
      // swallow — syncState is best-effort; event-driven state remains valid
    }

    return this.stateManager.getState();
  }

  /**
   * Gets the current configuration of the widget.
   *
   * @returns A readonly copy of the widget configuration
   *
   * @example
   * ```typescript
   * const config = widget.getConfig();
   * console.log('Mode:', config.mode);
   * console.log('API Key:', config.apiKey);
   * ```
   */
  getConfig(): Readonly<WidgetConfig> {
    return { ...this.config };
  }

  /**
   * Sets custom data to be passed to the AI agent.
   * This data is merged with any existing custom data and sent with each message.
   *
   * @param data - Key-value pairs of custom data
   * @fires YampaWidget#error - If setting custom data fails
   *
   * @example
   * ```typescript
   * // Set user context for personalized responses
   * widget.setCustomData({
   *   userId: 'user-123',
   *   userName: 'John Doe',
   *   subscription: 'premium',
   *   currentPage: '/products'
   * });
   * ```
   */
  setCustomData(data: Record<string, unknown>): void {
    this.agentCustomData = { ...this.agentCustomData, ...data };
    this.bridge
      .sendCommand({
        type: CommandType.SET_CUSTOM_DATA,
        payload: this.agentCustomData,
      })
      .catch((error) => {
        this.emit("error", {
          error: error instanceof Error ? error.message : "Unknown error",
          code: "SET_CUSTOM_DATA_FAILED",
        });
      });
  }

  /**
   * Starts a new conversation with the AI agent.
   * This creates a new conversation context and returns the conversation ID.
   *
   * @returns The ID of the newly created conversation
   * @throws {ValidationError} If the conversation could not be created
   * @fires YampaWidget#conversation:started - When the conversation is created
   * @fires YampaWidget#error - If starting the conversation fails
   *
   * @example
   * ```typescript
   * const conversationId = await widget.startConversation();
   * console.log('Started conversation:', conversationId);
   * ```
   */
  async startConversation(): Promise<string> {
    try {
      const result = await this.bridge.sendCommand({
        type: CommandType.START_CONVERSATION,
      });

      if (
        result &&
        typeof result === "object" &&
        "conversationId" in result &&
        typeof result.conversationId === "string"
      ) {
        this.stateManager.setConversationId(result.conversationId);
        this.stateManager.setMessageCount(0);
        this.stateManager.setIsClosed(false);
        return result.conversationId;
      }

      throw new ValidationError(
        "Failed to create conversation",
        "CONVERSATION_CREATE_FAILED"
      );
    } catch (error) {
      this.emit("error", {
        error: error instanceof Error ? error.message : "Unknown error",
        code: "START_CONVERSATION_FAILED",
      });
      throw error;
    }
  }

  /**
   * Gets all messages from the current conversation.
   *
   * @returns An array of messages in the conversation
   * @throws {Error} If fetching messages fails
   * @fires YampaWidget#error - If getting messages fails
   *
   * @example
   * ```typescript
   * const messages = await widget.getMessages();
   * messages.forEach(msg => {
   *   console.log(`${msg.type}: ${msg.content}`);
   * });
   * ```
   */
  async getMessages(): Promise<Message[]> {
    try {
      const result = await this.bridge.sendCommand({
        type: CommandType.GET_MESSAGES,
      });

      if (
        result &&
        typeof result === "object" &&
        "messages" in result &&
        Array.isArray(result.messages)
      ) {
        const r = result as Record<string, unknown>;
        if (typeof r.isClosed === "boolean") {
          this.stateManager.setIsClosed(r.isClosed);
        }
        return result.messages as Message[];
      }

      return [];
    } catch (error) {
      this.emit("error", {
        error: error instanceof Error ? error.message : "Unknown error",
        code: "GET_MESSAGES_FAILED",
      });
      throw error;
    }
  }

  async clearConversation(): Promise<void> {
    try {
      await this.bridge.sendCommand({
        type: CommandType.CLEAR_CONVERSATION,
      });
      this.stateManager.setConversationId(undefined);
      this.stateManager.setMessageCount(0);
      this.stateManager.setIsStreaming(false);
      this.stateManager.setIsClosed(false);
    } catch (error) {
      this.emit("error", {
        error: error instanceof Error ? error.message : "Unknown error",
        code: "CLEAR_CONVERSATION_FAILED",
      });
      throw error;
    }
  }

  /**
   * Signals the backend that the current conversation has ended.
   * Does not reset local state — the existing conversation ID and messages
   * remain available on the client.
   *
   * @fires YampaWidget#error - If the backend rejects the request
   *
   * @example
   * ```typescript
   * await widget.endConversation();
   * ```
   */
  async endConversation(): Promise<{ success: boolean }> {
    try {
      await this.bridge.sendCommand({
        type: CommandType.END_CONVERSATION,
      });
      return { success: true };
    } catch (error) {
      this.emit("error", {
        error: error instanceof Error ? error.message : "Unknown error",
        code: "END_CONVERSATION_FAILED",
      });
      throw error;
    }
  }

  private handleBridgeEvent(event: BridgeMessage): void {
    switch (event.type) {
      case EventType.READY:
        this.handleReadyEvent(event);
        break;
      case EventType.OPENED:
        this.handleOpenedEvent(event);
        break;
      case EventType.CLOSED:
        this.handleClosedEvent(event);
        break;
      case EventType.CONVERSATION_STARTED:
        this.handleConversationStartedEvent(event);
        break;
      case EventType.AI_STREAMING:
        this.handleAiStreamingEvent(event);
        break;
      case EventType.AI_DONE:
        this.handleAiDoneEvent(event);
        break;
      case EventType.MESSAGE_RECEIVED:
        this.handleMessageReceivedEvent(event);
        break;
      case EventType.MESSAGE_SENT:
        this.handleMessageSentEvent(event);
        break;
      case EventType.ERROR:
        this.handleErrorEvent(event);
        break;
      case EventType.CONFIG_LOADED:
        this.handleConfigLoadedEvent(event);
        break;
      default:
        break;
    }
  }

  private handleReadyEvent(_event: BridgeMessage): void {
    this.stateManager.setIsReady(true);
    this.bridge.sendCommand({
      type: CommandType.SET_INITIAL_CONFIG,
      payload: {
        apiKey: this.config.apiKey,
        agentCustomData: this.agentCustomData,
        stream: this.stream,
        mode: this.mode,
        overrides: this.overrides,
      },
    });

    this.syncState().catch(() => {
      // non-fatal; the SDK still tracks state via events
    });

    this.emit("ready", undefined);
  }

  private handleOpenedEvent(_event: BridgeMessage): void {
    if (this.mode === "bubble") {
      this.renderer.setBubbleCollapsed(false);
    }
    this.stateManager.setIsOpen(true);
    this.emit("open", undefined);
  }

  private handleClosedEvent(_event: BridgeMessage): void {
    if (this.mode === "bubble") {
      this.renderer.setBubbleCollapsed(true);
    }
    this.stateManager.setIsOpen(false);
    this.emit("close", undefined);
  }

  private handleConversationStartedEvent(event: BridgeMessage): void {
    if (
      event.payload &&
      typeof event.payload === "object" &&
      "conversationId" in event.payload &&
      typeof event.payload.conversationId === "string"
    ) {
      const isClosed =
        "isClosed" in event.payload && typeof event.payload.isClosed === "boolean"
          ? event.payload.isClosed
          : false;
      this.stateManager.setConversationId(event.payload.conversationId);
      this.stateManager.setIsClosed(isClosed);
      this.emit("conversation:started", {
        conversationId: event.payload.conversationId,
        ...(isClosed ? { isClosed } : {}),
      });
    }
  }

  private handleAiStreamingEvent(event: BridgeMessage): void {
    if (
      event.payload &&
      typeof event.payload === "object" &&
      "token" in event.payload &&
      typeof event.payload.token === "string"
    ) {
      this.stateManager.setIsStreaming(true);
      this.emit("ai:streaming", {
        token: event.payload.token,
        isMcp:
          "isMcp" in event.payload && typeof event.payload.isMcp === "boolean"
            ? event.payload.isMcp
            : undefined,
      });
    }
  }

  private handleAiDoneEvent(event: BridgeMessage): void {
    if (
      event.payload &&
      typeof event.payload === "object" &&
      "message" in event.payload
    ) {
      this.stateManager.setIsStreaming(false);
      this.emit("ai:done", {
        message: event.payload.message as Message,
      });
    }
  }

  private handleMessageReceivedEvent(event: BridgeMessage): void {
    if (
      event.payload &&
      typeof event.payload === "object" &&
      "message" in event.payload
    ) {
      this.stateManager.incrementMessageCount();
      this.emit("message:received", {
        message: event.payload.message as Message,
        timestamp: Date.now(),
      });
    }
  }

  private handleMessageSentEvent(event: BridgeMessage): void {
    if (
      event.payload &&
      typeof event.payload === "object" &&
      "message" in event.payload &&
      typeof event.payload.message === "string"
    ) {
      const timestamp =
        "timestamp" in event.payload &&
        typeof event.payload.timestamp === "number"
          ? event.payload.timestamp
          : Date.now();
      this.stateManager.incrementMessageCount();
      this.stateManager.setIsStreaming(true);
      this.emit("message:sent", {
        message: event.payload.message,
        timestamp,
      });
    }
  }

  private handleErrorEvent(event: BridgeMessage): void {
    this.stateManager.setIsStreaming(false);
    if (
      event.payload &&
      typeof event.payload === "object" &&
      "error" in event.payload
    ) {
      this.emit("error", {
        error:
          typeof event.payload.error === "string"
            ? event.payload.error
            : String(event.payload.error),
        code:
          "code" in event.payload && typeof event.payload.code === "string"
            ? event.payload.code
            : undefined,
      });
    }
  }

  private handleConfigLoadedEvent(event: BridgeMessage): void {
    if (event.payload && typeof event.payload === "object") {
      const { primaryColor, textColor } = event.payload as {
        primaryColor?: string;
        textColor?: string;
      };

      if (primaryColor || textColor) {
        this.config.theme = {
          ...this.config.theme,
          ...(primaryColor && { primaryColor }),
          ...(textColor && { textColor }),
        };
        this.renderer.updateConfig(this.config);
      }
    }
  }

  private shouldHandleBubbleClick(
    e: MouseEvent,
    wrapper: HTMLElement
  ): boolean {
    if (this.stateManager.isOpen()) {
      return false;
    }
    return (
      e.target === wrapper ||
      (e.target as HTMLElement).closest("[data-yampa-widget-indicator]") !==
        null
    );
  }

  private async handleBubbleClickOpen(): Promise<void> {
    try {
      await this.open();
    } catch (error) {
      console.error("[Widget] Failed to open widget:", error);
      this.emit("error", {
        error: error instanceof Error ? error.message : "Unknown error",
        code: "OPEN_FAILED",
      });
    }
  }

  /**
   * Destroys the widget and cleans up all resources.
   * This removes the widget from the DOM, closes all connections,
   * and removes all event listeners.
   *
   * After calling destroy(), the widget instance should not be used.
   *
   * @example
   * ```typescript
   * // Clean up when done
   * widget.destroy();
   * ```
   *
   * @example
   * ```typescript
   * // Clean up on page unload
   * window.addEventListener('beforeunload', () => {
   *   widget.destroy();
   * });
   * ```
   */
  destroy(): void {
    this.bridge.destroy();
    this.renderer.destroy();
    this.removeAllListeners();
    this.stateManager.reset();
  }
}
